import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeftCircle } from "react-icons/fi";
import { FaBriefcase, FaUserFriends } from "react-icons/fa";
import {
  MdDescription,
  MdOutlineHeight,
  MdChevronRight,
  MdOutlineZoomOutMap,
} from "react-icons/md";
import dayjs from "dayjs";
import { useDashboardContext } from "../../context/Dashboard_context";
import "./MyProfile.css";
import { getAgeFromDob } from "../../utils/helper";
import { RiExpandHorizontalSFill } from "react-icons/ri";
import { IoLocation, IoMoonSharp, IoSchoolOutline } from "react-icons/io5";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

// ─── MyProfile ────────────────────────────────────────────────────────────────
const MyProfile = () => {
  const navigate = useNavigate();
  const { setSelectedProfile } = useDashboardContext();
  const [profile, setProfile] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("myProfile");
    if (stored) {
      try {
        setProfile(JSON.parse(stored));
      } catch {
        setProfile(null);
      }
    }
  }, []);

  if (!profile) {
    return (
      <div className="myprofile-page">
        <header className="myprofile-header">
          <button
            className="myprofile-back-btn"
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <FiArrowLeftCircle size={30} />
          </button>
          <span className="myprofile-header-title">MY PROFILE</span>
          <div className="myprofile-header-spacer" />
        </header>
        <div className="myprofile-empty">
          <div className="myprofile-empty-icon">👤</div>
          <p className="myprofile-empty-title">No profile data found</p>
          <p className="myprofile-empty-sub">
            Please go back and select your profile
          </p>
        </div>
      </div>
    );
  }

  const allImages = [
    ...(profile.photo_url ? [{ src: profile.photo_url }] : []),
    ...(profile.other_images || []).map((img) => ({ src: img.image_url })),
  ];

  const openProfile = () => {
    setSelectedProfile(profile);
    navigate(`/details/${profile.id}`);
  };

  return (
    <div className="myprofile-page">
      {/* Header */}
      <header className="myprofile-header">
        <button
          className="myprofile-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <FiArrowLeftCircle size={30} />
        </button>
        <span className="myprofile-header-title">MY PROFILE</span>
        <div className="myprofile-header-spacer" />
      </header>

      {/* Main */}
      <main className="myprofile-main">
        <div className="myprofile-layout">
          {/* ── Left: Profile Card ── */}
          <div className="myprofile-card">
            {/* Photo Section */}
            <div className="myprofile-photo-wrapper">
              <div
                className="myprofile-photo-overlay"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxOpen(true);
                }}
              >
                <div className="myprofile-zoom-hint">
                  <MdOutlineZoomOutMap size={14} />
                  <span>Tap to zoom</span>
                </div>
              </div>

              {allImages.length > 0 ? (
                <div
                  className="myprofile-photo-slider"
                  onClick={() => {
                    setCurrentIdx(currentIdx);
                    setLightboxOpen(true);
                  }}
                >
                  <div
                    className="myprofile-photo-track"
                    style={{ transform: `translateX(-${currentIdx * 100}%)` }}
                  >
                    {allImages.map((img, i) => (
                      <img
                        key={i}
                        src={img.src}
                        alt={`${profile.name} ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 64,
                    color: "#fff",
                  }}
                >
                  👤
                </div>
              )}
            </div>

            {/* Card Body */}
            <div className="myprofile-card-body">
              <div className="myprofile-name-row">
                <span className="myprofile-name">{profile.name}</span>
                <span className="myprofile-age">
                  {getAgeFromDob(profile.dob)} yrs
                </span>
              </div>

              <div className="myprofile-stats-row">
                <div
                  className="myprofile-stat-box"
                  style={{
                    background: "linear-gradient(135deg,#f0f9ff,#e0f2fe)",
                  }}
                >
                  <span className="myprofile-stat-icon">
                    <MdOutlineHeight size={16} color="#0284c7" />
                  </span>
                  <span className="myprofile-stat-label">Height</span>
                  <span className="myprofile-stat-value">{profile.height}</span>
                </div>
                <div
                  className="myprofile-stat-box"
                  style={{
                    background: "linear-gradient(135deg,#fef3c7,#fde68a)",
                  }}
                >
                  <span className="myprofile-stat-icon">
                    <RiExpandHorizontalSFill size={18} color="#de7706" />
                  </span>
                  <span className="myprofile-stat-label">Weight</span>
                  <span className="myprofile-stat-value">{profile.weight}</span>
                </div>
                <div
                  className="myprofile-stat-box"
                  style={{
                    background: "linear-gradient(135deg,#fce7f3,#fbcfe8)",
                  }}
                >
                  <span className="myprofile-stat-icon">
                    <IoSchoolOutline size={16} color="#dd2777" />
                  </span>
                  <span className="myprofile-stat-label">Education</span>
                  <span
                    className="myprofile-stat-value"
                    style={{ fontSize: "11px" }}
                  >
                    {profile.education?.name || profile.education}
                  </span>
                </div>
              </div>

              <ul className="myprofile-info-list">
                <li className="myprofile-info-item">
                  <FaBriefcase
                    className="myprofile-info-icon profession"
                    size={14}
                  />
                  <span>{profile.job}</span>
                </li>
                <li className="myprofile-info-item">
                  <IoLocation
                    className="myprofile-info-icon location"
                    size={14}
                  />
                  <span>
                    {profile.city?.name}, {profile.state?.name}
                  </span>
                </li>
                <li className="myprofile-info-item">
                  <IoMoonSharp
                    className="myprofile-info-icon religion"
                    size={14}
                  />
                  <span>{profile.religion?.name || "N/A"}</span>
                </li>
                <li className="myprofile-info-item">
                  <FaUserFriends
                    className="myprofile-info-icon marital"
                    size={14}
                  />
                  <span>{profile.marital_status?.name || "N/A"}</span>
                </li>
              </ul>

              <button className="myprofile-details-btn" onClick={openProfile}>
                <MdDescription size={18} />
                View Full Details
                <MdChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="myprofile-right-panel">
            <div className="myprofile-panel-card">
              <div className="myprofile-panel-badge">👤 Profile Summary</div>
              <p className="myprofile-panel-title">Personal Details</p>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Full Name</span>
                <span className="myprofile-panel-val">{profile.name}</span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Age</span>
                <span className="myprofile-panel-val">
                  {getAgeFromDob(profile.dob)} years
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Date of Birth</span>
                <span className="myprofile-panel-val">
                  {profile.dob
                    ? dayjs(profile.dob).format("YYYY-MM-DD")
                    : "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Marital Status</span>
                <span className="myprofile-panel-val">
                  {profile.marital_status?.name || "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Religion</span>
                <span className="myprofile-panel-val">
                  {profile.religion?.name || "N/A"}
                </span>
              </div>
            </div>

            <div className="myprofile-panel-card">
              <p className="myprofile-panel-title">Location & Career</p>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">City</span>
                <span className="myprofile-panel-val">
                  {profile.city?.name || "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">State</span>
                <span className="myprofile-panel-val">
                  {profile.state?.name || "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Occupation</span>
                <span className="myprofile-panel-val">
                  {profile.job || "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Education</span>
                <span className="myprofile-panel-val">
                  {profile.education?.name || profile.education || "N/A"}
                </span>
              </div>
            </div>

            <div className="myprofile-panel-card">
              <p className="myprofile-panel-title">Physical Stats</p>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Height</span>
                <span className="myprofile-panel-val">
                  {profile.height || "N/A"}
                </span>
              </div>
              <div className="myprofile-panel-row">
                <span className="myprofile-panel-key">Weight</span>
                <span className="myprofile-panel-val">
                  {profile.weight || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={allImages}
        index={currentIdx}
        plugins={[Zoom]}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
          buttonZoom: () => null,
        }}
        carousel={{
          finite: true,
        }}
        zoom={{
          maxZoomPixelRatio: 4,
          zoomInMultiplier: 2,
          scrollToZoom: true,
          doubleTapDelay: 300,
        }}
        styles={{
          container: { backgroundColor: "rgba(0,0,0,1)" },
        }}
      />
    </div>
  );
};

export default MyProfile;
